import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../web/components-v2/Theme/theme';
import {
	background,
	borderOutline,
	brandPrimaryText,
	darkGrayFontColor,
	destructive,
	errorLight,
	header,
	lightGray,
	midGray,
	success,
	successTint,
	titles,
	white,
} from '../../../web/styles/colors';

export const styleSheet = StyleSheet.create({
	// Meeting Note Form Styles
	meetingNoteForm: {
		marginTop: 16,
	},
	formColumns: {
		display: 'grid',
		gridTemplateColumns: '1fr 400px',
		gap: 32,
		alignItems: 'flex-start',
	},
	formLeftColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: 20,
	},
	formRightColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		backgroundColor: white,
		padding: 16,
		paddingLeft: 10,
		borderRadius: 4,
	},
	formField: {
		marginBottom: 0,
	},
	formLabel: {
		display: 'block',
		fontSize: 14,
		fontWeight: 600,
		color: header,
		marginBottom: 8,
	},
	required: {
		color: destructive,
	},
	checkboxLabel: {
		display: 'flex',
		alignItems: 'center',
		fontSize: 14,
		color: header,
		cursor: 'pointer',
	},
	onboardingText: {
		marginLeft: 8,
	},
	richEditorWrapper: {
		border: `1px solid ${midGray}`,
		borderRadius: 4,
		overflow: 'hidden',
	},
	selectedAccountDisplay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: 10,
		backgroundColor: successTint,
		borderRadius: 4,
		border: `1px solid ${successTint}`,
	},
	changeAccountButton: {
		padding: '4px 12px',
		fontSize: 12,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${brandPrimaryText}`,
		backgroundColor: white,
		color: brandPrimaryText,
		':hover': {
			backgroundColor: brandPrimaryText,
			color: white,
		},
	},
	formActions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: 12,
		marginTop: 16,
	},
	cancelButton: {
		padding: '10px 24px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		color: header,
		':hover:not(:disabled)': {
			backgroundColor: lightGray,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	saveButton: {
		padding: '10px 24px',
		fontSize: 14,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: 'none',
		backgroundColor: brandPrimaryText,
		color: white,
		':hover:not(:disabled)': {
			opacity: 0.9,
		},
		':disabled': {
			opacity: 0.5,
			cursor: 'not-allowed',
		},
	},
	ccNotice: {
		fontSize: 12,
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginTop: 8,
		textAlign: 'right' as const,
	},
	validationErrors: {
		marginBottom: 16,
		padding: 12,
		backgroundColor: errorLight,
		borderRadius: 4,
		border: `1px solid ${errorLight}`,
	},
	validationError: {
		fontSize: 14,
		color: destructive,
		marginBottom: 4,
		':last-child': {
			marginBottom: 0,
		},
	},
	nextMeetingContainer: {
		display: 'flex',
		gap: 8,
		alignItems: 'center',
	},
	nextMeetingButton: {
		flex: 1,
		padding: 10,
		fontSize: 14,
		textAlign: 'left',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		cursor: 'pointer',
		color: header,
		':hover': {
			backgroundColor: lightGray,
		},
	},
	clearDateButton: {
		padding: '8px 16px',
		fontSize: 13,
		fontWeight: 600,
		cursor: 'pointer',
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		backgroundColor: white,
		color: darkGrayFontColor,
		':hover': {
			backgroundColor: lightGray,
		},
	},
	datePickerContainer: {
		marginTop: 8,
		padding: 16,
		backgroundColor: white,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
	},
	// Loading
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 32,
	},
	loadingText: {
		marginLeft: 16,
	},
	// Confirmation styles
	confirmationContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: '16px 30px 30px',
	},
	confirmationMessage: {
		textAlign: 'center',
		padding: 24,
		backgroundColor: background,
		borderRadius: 8,
	},
	confirmationTitle: {
		fontSize: 20,
		fontWeight: 600,
		color: titles,
		marginBottom: 16,
	},
	confirmationText: {
		fontSize: 16,
		lineHeight: 1.5,
		color: darkGrayFontColor,
	},
	confirmationTextWithMargin: {
		marginBottom: 20,
	},
	confirmationActions: {
		display: 'flex',
		gap: 16,
		justifyContent: 'center',
	},
	confirmationColumn: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		minWidth: 0,
	},
	confirmationColumnFull: {
		maxWidth: 'none',
		margin: 0,
	},
	confirmationPanel: {
		padding: 20,
		backgroundColor: background,
		borderRadius: 8,
		border: `1px solid ${borderOutline}`,
	},
	confirmationPanelTitle: {
		fontSize: 16,
		fontWeight: 600,
		color: titles,
		marginBottom: 16,
		marginTop: 0,
	},
	// Tab styles
	confirmationTabBar: {
		display: 'flex',
		gap: 4,
		marginBottom: 24,
		borderBottom: `1px solid ${borderOutline}`,
		paddingBottom: 0,
	},
	confirmationTab: {
		padding: '12px 20px',
		fontSize: 14,
		fontWeight: 500,
		color: darkGrayFontColor,
		backgroundColor: 'transparent',
		border: 'none',
		borderBottom: '2px solid transparent',
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		marginBottom: -1,
		':hover': {
			color: brandPrimaryText,
			backgroundColor: background,
		},
	},
	confirmationTabActive: {
		color: brandPrimaryText,
		borderBottomColor: brandPrimaryText,
		fontWeight: 600,
	},
	confirmationTabCompleted: {
		color: success,
	},
	confirmationTabContent: {
		minHeight: 400,
	},
	// Deep Research fields
	deepResearchFields: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		marginBottom: 16,
	},
	deepResearchFieldLabel: {
		display: 'block',
		fontSize: 13,
		fontWeight: 600,
		color: darkGrayFontColor,
		marginBottom: 4,
	},
	deepResearchFieldInput: {
		width: '100%',
		padding: '8px 12px',
		fontSize: 14,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: lightGray,
			cursor: 'not-allowed',
		},
	},
	// Summary display
	summaryHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 8,
	},
	summaryNote: {
		fontSize: 13,
		color: darkGrayFontColor,
		fontStyle: 'italic',
		marginBottom: 8,
	},
	summaryContent: {
		padding: 16,
		backgroundColor: background,
		borderRadius: 8,
		whiteSpace: 'pre-wrap',
		maxHeight: 400,
		overflowY: 'auto',
		fontFamily: 'monospace',
		fontSize: 13,
		lineHeight: 1.6,
		border: `1px solid ${borderOutline}`,
	},
	// Content Team editor
	contentTeamEditorContainer: {
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		overflow: 'hidden',
		backgroundColor: white,
		':nth-child(1n) .tox-editor-header': {
			borderBottom: `1px solid ${borderOutline}`,
		},
	},
	// Account Profile styles
	accountProfileFields: {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		marginBottom: 16,
	},
	accountProfileField: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	accountProfileFieldGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		gap: THEME.spacing.md,
	},
	accountProfileFieldLabel: {
		display: 'block',
		fontSize: '13px',
		fontWeight: 600,
		color: darkGrayFontColor,
		marginBottom: '4px',
	},
	accountProfileFieldInput: {
		width: '100%',
		padding: '8px 12px',
		fontSize: '14px',
		borderRadius: '4px',
		border: `1px solid ${midGray}`,
		boxSizing: 'border-box',
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: '#f5f5f5',
			cursor: 'not-allowed',
		},
	},
	accountProfileTextarea: {
		width: '100%',
		padding: '10px 12px',
		fontSize: 14,
		lineHeight: 1.6,
		borderRadius: 4,
		border: `1px solid ${midGray}`,
		fontFamily: 'inherit',
		resize: 'vertical',
		boxSizing: 'border-box' as const,
		':focus': {
			outline: 'none',
			borderColor: brandPrimaryText,
		},
		':disabled': {
			backgroundColor: background,
			cursor: 'not-allowed',
		},
	},
	// Recap Email styles
	recapEmailEditorContainer: {
		border: `1px solid ${borderOutline}`,
		borderRadius: 8,
		overflow: 'hidden',
		backgroundColor: white,
		':nth-child(1n) .tox-editor-header': {
			borderBottom: `1px solid ${borderOutline}`,
		},
	},
});
